// @flow

import * as React from 'react';
import type { ChildrenArray, Element } from 'react';
import { Component } from 'react';
import styled from 'styled-components';
import { Text } from '../Text';
import { Item } from './Item';
import { Arrow } from './Arrow';
import Downshift from 'downshift';
import _ from 'lodash';

type DropdownProps = {
  id: string,
  onChange: (newValue: string) => void,
  onSelect?: (newvalue: string) => void,
  disabled?: boolean,
  pageSize: number,
  placeholder?: string,
  children: ChildrenArray<Element<typeof Item>>, // array of Item
  selectedValue: string,
  label?: any,
  error?: boolean,
  onClick?: () => void,
};

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const MenuContainer = styled.div`
  border: solid 1px #dbe0de;
  border-radius: 2px;
  margin-top: 12px;
  position: absolute;
  width: 100%;
  z-index: 1;
  &:before {
    position: absolute;
    overflow: hidden;
    right: 15px;
    content: '';
    height: 0;
    width: 0;
    top: -17px;
    border-style: outset outset solid outset;
    border: 8px solid transparent;
    border-bottom-color: #dbe0de;
  }
  &:after {
    position: absolute;
    overflow: hidden;
    right: 15px;
    content: '';
    height: 0;
    width: 0;
    top: -16px;
    border-style: outset outset solid outset;
    border: 8px solid transparent;
    border-bottom-color: white;
  }
  &:nth-child(even) {
    background-color: ${(props) => (props.isFocused ? '#bfcdd4' : '#f9fafa')};
  }
`;

const DropdownContainer = styled.div`
  height: 48px;
  width: 100%;
  border-radius: 2px;
  background-image: linear-gradient(to bottom, #ffffff, #fafafa);
  display: flex;
  border: solid 1px #dbe0de;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  border-color: ${(props) => (props.error ? props.theme.errorColor : '')};
`;

const TextContainer = styled.div`
  flex-direction: row;
  display: flex;
  flex: 1;
  align-items: center;
  padding: 0;
`;

const ControllerButton = styled.div`
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: space-between;
  flex: 1;
  font-size: 1rem;
  padding: 0.75rem 1.25rem 0.75rem 1.125rem;
`;

const StyledItem = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  flex: 1;
  flex-direction: row;
  padding: 12px 18px;
  min-height: 48px;
  &:nth-child(odd) {
    background-color: ${(props) =>
      props.isActive || props.isSelected ? '#bfcdd4' : 'white'};
  }
  &:nth-child(even) {
    background-color: ${(props) =>
      props.isActive || props.isSelected ? '#bfcdd4' : '#f9fafa'};
  }
`;

const StyledLabel = styled.label`
  font-size: 0.875rem;
  line-height: 1.29;
  color: ${(props) => props.theme.textColor};
  display: block;
  margin-bottom: 0.5rem;
`;

export default class Dropdown extends Component<DropdownProps> {
  static Item = Item;

  static defaultProps = {
    pageSize: 5,
    onChange: (newValue: string) => {},
  };

  itemToString = (item: any) => {
    return item ? item.displayText : '';
  };

  renderDropdown(
    items: ChildrenArray<Element<typeof Item>>,
    selItem: string | Element<typeof Item>
  ) {
    return (
      <Downshift
        onChange={this.props.onChange}
        onSelect={this.props.onSelect}
        selectedItem={selItem}
        itemToString={this.itemToString}
      >
        {({
          getRootProps,
          getItemProps,
          getLabelProps,
          getToggleButtonProps,
          isOpen,
          highlightedIndex,
          clearSelection,
          itemToString,
          selectedItem,
        }) => (
          <Container {...getRootProps({ refKey: 'ref' })}>
            {this.props.label && (
              <StyledLabel {...getLabelProps()}>{this.props.label}</StyledLabel>
            )}
            <DropdownContainer error={this.props.error} id={this.props.id}>
              <ControllerButton
                {...getToggleButtonProps({
                  placeholder: this.props.placeholder,
                  onClick: this.props.onClick,
                })}
              >
                <TextContainer>
                  <Text>
                    {(selectedItem &&
                      selectedItem.props &&
                      selectedItem.props.displayText) ||
                      this.props.placeholder}
                  </Text>
                </TextContainer>
                {!this.props.disabled ? (
                  <div style={{ alignSelf: 'center' }}>
                    <Arrow isOpen={isOpen} />
                  </div>
                ) : null}
              </ControllerButton>
            </DropdownContainer>
            {isOpen && !this.props.disabled ? (
              <MenuContainer>
                {React.Children.map(items, (item, index) => {
                  return (
                    item && (
                      <StyledItem
                        id={
                          item.props.id ||
                          this.props.id.concat(item.props.value)
                        }
                        {...getItemProps({
                          key: item.props.value,
                          item: item.props.value,
                          index: index,
                          isActive: highlightedIndex === index,
                          isSelected:
                            selectedItem &&
                            selectedItem.value === item.props.value,
                          style: {
                            fontWeight:
                              this.props.selectedValue === item.props.value
                                ? 'bold'
                                : 'normal',
                          },
                        })}
                      >
                        {item}
                      </StyledItem>
                    )
                  );
                })}
              </MenuContainer>
            ) : null}
          </Container>
        )}
      </Downshift>
    );
  }

  render() {
    const selectedItem: string | Element<typeof Item> = _.find(
      React.Children.toArray(this.props.children),
      (item) => item.props.value === this.props.selectedValue
    );
    return this.renderDropdown(
      this.props.children,
      selectedItem ? selectedItem : ''
    );
  }
}
